const SEPARATOR = '\u2063\u2063\u2063';
const MAX_CHARS = 4000;
const IS_DEV = process.env.NODE_ENV !== 'production';
const LOG_PREFIX = '[translate]';

// Single text translation
export const translate = async (text: string, lang: string): Promise<string> => {
  try {
    const res = await fetch(
      `https://translate.googleapis.com/translate_a/single?client=gtx&sl=auto&tl=${lang}&dt=t&q=${encodeURIComponent(text)}`
    );
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    return data[0].map((item: any) => item[0]).join('');
  } catch (err) {
    if (IS_DEV) {
      // Surface the failure so translation outages aren't invisible.
      console.warn(`${LOG_PREFIX} single-translate failed for "${text.slice(0, 40)}..." -> ${lang}:`, err);
    }
    return text;
  }
};

// Batch translation — splits into chunks to avoid 400 errors
export const translateBatch = async (texts: string[], lang: string): Promise<string[]> => {
  if (!texts.length) return [];

  // Split into chunks under MAX_CHARS
  const chunks: string[][] = [];
  let currentChunk: string[] = [];
  let currentLength = 0;

  texts.forEach((text) => {
    const textLength = text.length + SEPARATOR.length;

    if (currentLength + textLength > MAX_CHARS && currentChunk.length > 0) {
      chunks.push(currentChunk);
      currentChunk = [];
      currentLength = 0;
    }

    currentChunk.push(text);
    currentLength += textLength;
  });

  if (currentChunk.length > 0) chunks.push(currentChunk);

  // Translate all chunks in parallel
  const results = await Promise.all(chunks.map((chunk) => translateChunk(chunk, lang)));

  return results.flat();
};

// Single chunk translation
const translateChunk = async (texts: string[], lang: string): Promise<string[]> => {
  try {
    const joined = texts.join(SEPARATOR);

    const res = await fetch(
      `https://translate.googleapis.com/translate_a/single?client=gtx&sl=auto&tl=${lang}&dt=t&q=${encodeURIComponent(joined)}`
    );

    if (!res.ok) throw new Error(`Google API error: ${res.status}`);

    const data = await res.json();
    const translated = data[0].map((item: any) => item[0]).join('');

    // Split by invisible separator
    const splitResult = translated.split(SEPARATOR);

    // If split count doesn't match — fallback to individual translation.
    // Google occasionally strips the invisible separators.
    if (splitResult.length !== texts.length) {
      if (IS_DEV) {
        console.warn(
          `${LOG_PREFIX} batch split mismatch (${splitResult.length}/${texts.length}), falling back to per-item translate for ${lang}`
        );
      }
      return await Promise.all(texts.map((t) => translate(t, lang)));
    }

    return splitResult;
  } catch (err) {
    if (IS_DEV) {
      console.warn(`${LOG_PREFIX} batch translate failed (${texts.length} items -> ${lang}):`, err);
    }
    // On error — fallback to individual translation, not originals
    return await Promise.all(texts.map((t) => translate(t, lang)));
  }
};
